import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { SosLogo } from "@/components/SosLogo";

export default function CustomerLogin() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            toast.success("Sessão iniciada");
            nav("/conta");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Credenciais inválidas");
        } finally {
            setBusy(false);
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const startGoogleLogin = () => {
        const redirectUrl = window.location.origin + "/conta/auth-callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="container-edge py-16 grid lg:grid-cols-[1fr_400px] gap-10 items-start" data-testid="customer-login-page">
            <div className="hidden lg:block">
                <div className="label-mono text-magenta">Área de Cliente</div>
                <h1 className="font-display font-black text-5xl tracking-tighter mt-2 leading-tight">
                    Bem-vindo de volta.
                </h1>
                <p className="mt-4 text-ink/70 max-w-md">
                    Aceda à sua conta para consultar encomendas, ver estado de envio e descarregar
                    facturas em PDF.
                </p>
                <div className="mt-10 border-2 border-ink p-6 bg-white">
                    <SosLogo size={56} />
                </div>
            </div>
            <form onSubmit={submit} className="border-2 border-ink bg-white p-8 shadow-[8px_8px_0_0_#0A0A0A]">
                <h2 className="font-display font-black text-2xl mb-5">Entrar</h2>

                <button
                    type="button"
                    onClick={startGoogleLogin}
                    className="w-full mb-5 border-2 border-ink bg-white py-3 px-4 font-bold flex items-center justify-center gap-3 hover:bg-paper transition-colors"
                    data-testid="google-login-btn"
                >
                    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                    </svg>
                    Continuar com Google
                </button>

                <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-ink/20" />
                    <span className="text-xs font-mono text-ink/50 uppercase tracking-wider">ou</span>
                    <div className="flex-1 h-px bg-ink/20" />
                </div>

                <label className="block mb-4">
                    <span className="label-mono mb-2 block">Email</span>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-edge"
                        required
                        data-testid="customer-email-input"
                    />
                </label>
                <label className="block mb-2">
                    <span className="label-mono mb-2 block">Password</span>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-edge"
                        required
                        data-testid="customer-password-input"
                    />
                </label>
                <div className="text-right mb-5">
                    <Link
                        to="/conta/recuperar-senha"
                        className="text-xs font-mono text-ink/60 hover:text-magenta"
                        data-testid="customer-forgot-link"
                    >
                        Esqueceu-se da senha?
                    </Link>
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    className="btn-primary w-full disabled:opacity-50"
                    data-testid="customer-login-btn"
                >
                    {busy && <Loader2 className="animate-spin" size={16} />}
                    {busy ? "A entrar…" : "Entrar"}
                </button>
                <div className="mt-5 text-sm font-mono text-center">
                    Ainda não tem conta?{" "}
                    <Link to="/conta/registar" className="text-magenta font-bold underline-offset-4 hover:underline">
                        Registe-se
                    </Link>
                </div>
            </form>
        </div>
    );
}
